import type { QueryClient } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { Cart, useLocalStorage, UserLocale } from 'hooks/userPreferences';
import type {
  AddCartItemMutation,
  CartLineInput,
  CreateCartMutation,
} from 'services/shopify';
import { ShopifyService } from 'services/shopify';
import { convertToCountryCodeEnum } from 'utils/convertToCountryCodeEnum';
import { mapShopifyCart } from 'utils/mappers/mapShopifyCart';

import { CART_KEY } from './useCart';

type CartAdd = NonNullable<
  NonNullable<AddCartItemMutation['cartLinesAdd']>['cart']
>;
type CartCreate = NonNullable<
  NonNullable<CreateCartMutation['cartCreate']>['cart']
>;

export async function addItem(
  lineItem: CartLineInput,
  country: string,
  setCartId: (cartId: { id: string }) => void,
  getCartId: () => { id?: string } | null,
): Promise<CartAdd | CartCreate | undefined | null> {
  const { id: cartId } = getCartId() || {};

  if (cartId) {
    try {
      const response = await ShopifyService.addCartItem({
        cartId,
        lines: [lineItem],
      });

      if (response.cartLinesAdd?.cart) {
        setCartId({ id: response.cartLinesAdd.cart.id });
        return response.cartLinesAdd.cart;
      }
    } catch (error) {
      console.error(error);
      throw new Error('Error adding item');
    }
  }

  const { cartCreate } = await ShopifyService.createCart({
    input: {
      buyerIdentity: {
        countryCode: convertToCountryCodeEnum(country),
      },
      lines: [lineItem],
    },
  });

  if (cartCreate?.cart?.id) {
    setCartId({ id: cartCreate.cart.id });
  }

  return cartCreate?.cart;
}

export const useAddItem = (queryClient: QueryClient) => {
  const {
    value: { countryCode },
  } = useLocalStorage(UserLocale);
  const { getValue, setStoredValue: setCartId } = useLocalStorage(Cart);

  return useMutation({
    mutationFn: (lineItem: CartLineInput) =>
      addItem(lineItem, countryCode, setCartId, getValue),
    onSuccess: (cart) => {
      if (cart) {
        const formattedCart = mapShopifyCart(cart);
        queryClient.setQueriesData(
          { queryKey: [CART_KEY, countryCode] },
          formattedCart,
        );
      }
    },
  });
};
